import React, { useState } from "react";
import { Button, Form } from "react-bootstrap";

import CustomModal from "../CustomModal";
import ResendOtp from "../ResendOtp";
import { API } from "../../../utils/api";
import { showToast } from "../../../utils/helper";
import { trackEvent } from "../../../utils/analytics";

/**
 * Reusable OTP-verification modal for the landing wizard.
 *
 * Two contexts:
 *   - Post-signup (default): user just claimed a spot; verify their email
 *     via /user/verify-email to finish the signup.
 *   - Step-1 lead: returning-user identity gate. Caller passes verifyFn
 *     that calls /user/verify-otp-lead, and the response carries the
 *     draft spot (if any) for auto-prefill on step 2.
 *
 * verifyFn(payload) and resendFn(payload) let the caller swap the API
 * calls without forking the UI. Both default to the post-signup behavior
 * so existing usages keep working.
 *
 * Modal can't be dismissed by clicking outside (no onClose handed to
 * CustomModal); users either verify or they bail later from the auth flow.
 */
const ClaimVerifyModal = ({
  show,
  email,
  onVerified,
  verifyFn,
  resendFn,
}) => {
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const verify = verifyFn || ((payload) => API.verfiyEmail(payload));
  const resend = resendFn || ((payload) => API.resendSignupOtp(payload));

  const submit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (loading) return;

    const trimmed = String(otp).trim();
    if (!trimmed) {
      setError("Please enter the verification code");
      return;
    }
    if (trimmed.length !== 6) {
      setError("Please enter a valid 6-digit code");
      return;
    }

    setError("");
    setLoading(true);

    verify({ email, otp: trimmed })
      .then((res) => {
        if (res && res.type === "success") {
          trackEvent("web_signup_verified", { method: "email", flow: "claim" });
          showToast("success", res.message || "Email verified");
          if (typeof onVerified === "function") onVerified(res);
        } else {
          const msg = (res && res.message) || "Verification failed";
          setError(msg);
          showToast("error", msg);
        }
      })
      .catch((err) => {
        const msg = (err && err.message) || "Verification failed";
        setError(msg);
        showToast("error", msg);
      })
      .finally(() => setLoading(false));
  };

  return (
    <CustomModal
      show={show}
      title="Verify your email"
      className="add-vehicle-modal claim-verify-modal"
    >
      <p className="text-muted mb-3">
        We sent a 6-digit code to <strong>{email}</strong>. Enter it below to
        finish setting up your account.
      </p>

      <Form onSubmit={submit}>
        <Form.Group className="mb-3">
          <Form.Label className="fw-bold">Verification code</Form.Label>
          <Form.Control
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="123456"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ""))}
            isInvalid={!!error}
            autoFocus
          />
          {error && (
            <Form.Control.Feedback type="invalid">
              {error}
            </Form.Control.Feedback>
          )}
        </Form.Group>

        <ResendOtp
          onResend={() =>
            resend({ email }).then((res) => {
              if (res && res.type === "success") {
                showToast(
                  "success",
                  res.message || "Verification code sent",
                );
              } else {
                showToast(
                  "error",
                  (res && res.message) || "Failed to resend code",
                );
              }
            })
          }
        />

        <div className="d-grid mt-4">
          <Button
            type="submit"
            variant=""
            className="new-vehicle-submit"
            disabled={loading}
          >
            <span>
              {loading && <i className="fas fa-spin fa-spinner me-2" />}
              {loading ? "Verifying…" : "Verify & continue"}
            </span>
          </Button>
        </div>
      </Form>
    </CustomModal>
  );
};

export default ClaimVerifyModal;
